/**
 * Drift Timeline (doc 147 §6.6 E8 + PR Q3 scrubber + compare).
 *
 * PR Q3 adds a visual scrubber over doc anchors with a commit card,
 * play/step controls, and a compare-mode toggle that diffs two anchors
 * side-by-side. The data source is still the docRef proxy (fallback
 * banner declares it); a future snapshot-store swap is mechanical.
 */
import { useMemo, useState, useEffect } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  buildDriftScrubber,
  compareDriftPoints,
  type DriftTick,
} from '../state/driftTimeline.js';
import { useFocusScroll } from '../state/useFocusScroll.js';
import { Icon } from '../components/Icon.js';
import { EntityChip } from '../components/EntityChip.js';
import { DRIFT_PLAY_MS as PLAY_MS } from '../state/timings.js';

export interface DriftTimelineViewProps {
  manifest: AtlasManifest;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id to scroll to + highlight. */
  focus?: string;
}

export function DriftTimelineView({
  manifest,
  onNavigateEntity,
  focus,
}: DriftTimelineViewProps): React.JSX.Element {
  const scrubber = useMemo(() => buildDriftScrubber(manifest), [manifest]);
  const [selected, setSelected] = useState<number>(scrubber.ticks.length - 1);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIdx, setCompareIdx] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  useFocusScroll(focus);

  // Auto-play.
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => {
      setSelected((s) => {
        if (s >= scrubber.ticks.length - 1) {
          setPlaying(false);
          return s;
        }
        return s + 1;
      });
    }, PLAY_MS);
    return () => window.clearInterval(t);
  }, [playing, scrubber.ticks.length]);

  const tick: DriftTick | undefined = scrubber.ticks[selected];
  const compareTick: DriftTick | undefined =
    compareIdx !== null ? scrubber.ticks[compareIdx] : undefined;
  const diff = useMemo(() => {
    if (!compareMode || !tick || !compareTick) return null;
    return compareDriftPoints(manifest, compareTick.docNum, tick.docNum);
  }, [compareMode, tick, compareTick, manifest]);

  const maxChange = Math.max(1, ...scrubber.ticks.map((t) => t.entityChangeCount));

  if (scrubber.ticks.length === 0) {
    return (
      <div className="drift-timeline">
        <h2>Drift Timeline</h2>
        <p className="subtitle">No entities carry a numeric docRef prefix — drift scrubber not available.</p>
      </div>
    );
  }

  return (
    <div className="drift-timeline">
      <h2>Drift Timeline</h2>
      <p className="subtitle">
        Doc-anchor scrubber over the manifest. Each tick = a doc; height
        = entities introduced. Click a tick to inspect, ⏯ to auto-play,
        or ↔ Compare to diff two points side-by-side.
        {scrubber.fallback && (
          <span className="drift-fallback-note">
            {' '}<Icon name="info" size="sm" /> Using docRef proxy — snapshot store not yet wired.
          </span>
        )}
      </p>

      <section className="drift-scrubber" aria-label="Drift scrubber">
        <header className="drift-scrubber-controls">
          <span className="drift-scrubber-label">
            MANIFEST SNAPSHOTS · doc {scrubber.range.firstDoc}–{scrubber.range.lastDoc}
          </span>
          <div className="drift-scrubber-buttons">
            <button
              type="button"
              className="btn btn-ghost"
              aria-label="Step back"
              onClick={() => setSelected((s) => Math.max(0, s - 1))}
              disabled={selected <= 0}
            >
              ⏮
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              aria-label={playing ? 'Pause' : 'Play'}
              onClick={() => {
                if (playing) setPlaying(false);
                else {
                  setSelected(0);
                  setPlaying(true);
                }
              }}
            >
              {playing ? '⏸' : '⏯'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              aria-label="Step forward"
              onClick={() => setSelected((s) => Math.min(scrubber.ticks.length - 1, s + 1))}
              disabled={selected >= scrubber.ticks.length - 1}
            >
              ⏭
            </button>
            <button
              type="button"
              className={`btn btn-primary drift-compare-toggle${compareMode ? ' is-active' : ''}`}
              aria-pressed={compareMode}
              onClick={() => {
                setCompareMode((v) => !v);
                if (!compareMode) setCompareIdx(0);
                else setCompareIdx(null);
              }}
            >
              ↔ Compare
            </button>
          </div>
        </header>
        <div className="drift-scrubber-track">
          {scrubber.ticks.map((t, i) => {
            const isSelected = i === selected;
            const isCompare = compareMode && i === compareIdx;
            const heightPct = Math.max(20, (t.entityChangeCount / maxChange) * 100);
            return (
              <button
                key={t.docNum}
                type="button"
                className={`drift-scrubber-tick${isSelected ? ' is-selected' : ''}${isCompare ? ' is-compare' : ''}`}
                style={{ height: `${heightPct}%` }}
                onClick={() => {
                  if (compareMode) setCompareIdx(i);
                  else setSelected(i);
                }}
                aria-label={`${t.label} · ${t.entityChangeCount} entities`}
                title={`${t.label} · ${t.entityChangeCount} entities`}
              />
            );
          })}
        </div>
        <div className="drift-scrubber-axis">
          <span>{scrubber.ticks[0]?.label}</span>
          <span>tick height = entities changed</span>
          <span>{scrubber.ticks[scrubber.ticks.length - 1]?.label}</span>
        </div>
        {tick && !compareMode && (
          <div className="drift-commit-card">
            <span className="drift-commit-sha mono">{tick.sha}</span>
            <div className="drift-commit-body">
              <div className="drift-commit-msg">{tick.label}</div>
              <div className="drift-commit-meta mono">
                {tick.entityChangeCount} entities affected
              </div>
            </div>
          </div>
        )}
      </section>

      {!compareMode && tick && (
        <section className="drift-anchor" aria-label={`Entities introduced at ${tick.label}`}>
          <h3 className="drift-anchor-head">
            <span className="mono">{tick.label}</span>
            <span className="drift-anchor-count">{tick.entityChangeCount}</span>
          </h3>
          <div className="drift-anchor-entities">
            {tick.entityIds.map((id) => (
              <EntityChip key={id} entityId={id} manifest={manifest} onClick={onNavigateEntity} />
            ))}
          </div>
        </section>
      )}

      {compareMode && diff && tick && compareTick && (
        <section className="drift-compare" aria-label="Compare result">
          <h3 className="drift-compare-head">
            Comparing <span className="chip mono">{compareTick.label}</span>
            <span className="drift-compare-sep">↔</span>
            <span className="chip brand mono">{tick.label}</span>
          </h3>
          <div className="drift-compare-cols">
            <div className="drift-compare-col">
              <header>Added · {diff.added.length}</header>
              {diff.added.length === 0 ? (
                <p className="drift-compare-empty">No entities added.</p>
              ) : (
                <div className="drift-anchor-entities">
                  {diff.added.map((id) => (
                    <EntityChip key={id} entityId={id} manifest={manifest} onClick={onNavigateEntity} />
                  ))}
                </div>
              )}
            </div>
            <div className="drift-compare-col">
              <header>Removed · {diff.removed.length}</header>
              <p className="drift-compare-empty">
                docRef proxy is append-only — removals only surface once the
                snapshot store wires in.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
